import React from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Construction } from 'lucide-react';
import { DashboardLayout } from './layouts/DashboardLayout';
import { ProtectedFeature } from './components/ProtectedFeature';
import { ProtectedRoute } from './components/ProtectedRoute';
import { InfoStateCard } from './components/ui/InfoStateCard';
import { PageLoader } from './components/ui/PageLoader';
import { fetchJsonWithAuth } from './lib/authFetch';
import { completePendingRegistration } from './lib/registration';
import type { Session } from '@supabase/supabase-js';
import type { SubscriptionFeature, TallerSubscriptionContext } from './lib/subscription';
import { useAuthStore } from './store/authStore';
import { supabase } from './lib/supabase';

const Landing = React.lazy(() => import('./pages/Landing'));
const Login = React.lazy(() => import('./pages/Login'));
const Register = React.lazy(() => import('./pages/Register'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const TermsAndConditions = React.lazy(() => import('./pages/TermsAndConditions'));
const SuperAdminDashboard = React.lazy(() => import('./pages/superadmin/Dashboard'));
const SuperAdminTalleres = React.lazy(() => import('./pages/superadmin/Talleres'));
const SuperAdminPlanes = React.lazy(() => import('./pages/superadmin/Planes'));
const SuperAdminBilling = React.lazy(() => import('./pages/superadmin/Billing'));
const SuperAdminPlatformConfig = React.lazy(() => import('./pages/superadmin/PlatformConfig'));
const AdminDashboard = React.lazy(() => import('./pages/admin/Dashboard'));
const AdminOrdenes = React.lazy(() => import('./pages/admin/Ordenes'));
const AdminRecordatorios = React.lazy(() => import('./pages/admin/Recordatorios'));
const AdminInventario = React.lazy(() => import('./pages/admin/Inventario'));
const AdminCitas = React.lazy(() => import('./pages/admin/Citas'));
const AdminClientes = React.lazy(() => import('./pages/admin/Clientes'));
const AdminVehiculos = React.lazy(() => import('./pages/admin/Vehiculos'));
const AdminCotizaciones = React.lazy(() => import('./pages/admin/Cotizaciones'));
const AdminSettings = React.lazy(() => import('./pages/admin/Settings'));
const AdminSubscription = React.lazy(() => import('./pages/admin/Subscription'));
const AdminUpgrade = React.lazy(() => import('./pages/admin/Upgrade'));
const AdminManual = React.lazy(() => import('./pages/admin/Manual'));
const ClientDashboard = React.lazy(() => import('./pages/client/Dashboard'));
const ClientPagos = React.lazy(() => import('./pages/client/Pagos'));
const ClientCitas = React.lazy(() => import('./pages/client/Citas'));
const ClientCotizaciones = React.lazy(() => import('./pages/client/Cotizaciones'));
const ClientHistorial = React.lazy(() => import('./pages/client/Historial'));
const ClientVehiculos = React.lazy(() => import('./pages/client/Vehiculos'));
const OrdenDetalle = React.lazy(() => import('./pages/shared/OrdenDetalle'));

function UnderConstructionState({
  title,
  description,
  backTo,
  backLabel,
}: {
  title: string;
  description: string;
  backTo: string;
  backLabel: string;
}) {
  return (
    <div className="flex min-h-[calc(100vh-10rem)] items-center justify-center">
      <InfoStateCard
        eyebrow="Modulo en preparacion"
        title={title}
        description={description}
        icon={<Construction className="h-7 w-7" />}
        action={
          <>
            <Link
              to={backTo}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-3 text-sm font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
            >
              <ArrowLeft className="h-4 w-4" />
              {backLabel}
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-3 text-sm font-medium text-white transition hover:bg-emerald-700"
            >
              Ir al inicio
              <ArrowRight className="h-4 w-4" />
            </Link>
          </>
        }
      />
    </div>
  );
}

function AdminFeatureRoute({
  allowedRoles,
  children,
  feature,
}: {
  allowedRoles?: string[];
  children: React.ReactNode;
  feature: SubscriptionFeature;
}) {
  return (
    <DashboardLayout>
      <ProtectedFeature feature={feature} allowedRoles={allowedRoles}>
        {children}
      </ProtectedFeature>
    </DashboardLayout>
  );
}

export default function App() {
  const { session, user, setSession, setUser, setInitialized } = useAuthStore();

  const fetchUserProfile = React.useCallback(async (userId: string, currentSession?: Session | null) => {
    try {
      let { data, error } = await supabase
        .from('users')
        .select('id, name, email, role, taller_id')
        .eq('id', userId)
        .maybeSingle();

      if (!data || error) {
        await completePendingRegistration(currentSession || useAuthStore.getState().session);
        const retry = await supabase
          .from('users')
          .select('id, name, email, role, taller_id')
          .eq('id', userId)
          .maybeSingle();
        data = retry.data;
        error = retry.error;
      }

      if (!data || error) {
        setUser(null);
        return;
      }

      let taller: TallerSubscriptionContext | null = null;
      if (data.taller_id && (data.role === 'admin_taller' || data.role === 'tecnico')) {
        try {
          taller = await fetchJsonWithAuth<TallerSubscriptionContext>('/api/taller/subscription-context');
        } catch (subscriptionError) {
          console.warn('No se pudo cargar el contexto de suscripción del taller.', subscriptionError);
        }
      }

      setUser({ ...data, taller });
    } catch (err) {
      console.error('Error fetching user profile:', err);
      setUser(null);
    }
  }, [setUser]);

  React.useEffect(() => {
    const initAuth = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      setSession(session);
      if (session?.user) {
        await fetchUserProfile(session.user.id, session);
      } else {
        setUser(null);
      }
      setInitialized(true);
    };

    initAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session?.user) {
        void fetchUserProfile(session.user.id, session);
      } else {
        setUser(null);
      }
      setInitialized(true);
    });

    return () => subscription.unsubscribe();
  }, [fetchUserProfile, setInitialized, setSession, setUser]);

  React.useEffect(() => {
    if (!session?.user?.id) return;
    if (user?.role !== 'admin_taller' && user?.role !== 'tecnico') return;

    const refreshProfile = () => {
      void fetchUserProfile(session.user.id);
    };

    const intervalId = window.setInterval(refreshProfile, 5 * 60 * 1000);
    const handleFocus = () => refreshProfile();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshProfile();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchUserProfile, session?.user?.id, user?.role]);

  return (
    <BrowserRouter>
      <React.Suspense
        fallback={
          <PageLoader
            title="Cargando modulo"
            description="Preparamos la siguiente pantalla para mantener la app mas ligera y rapida."
          />
        }
      >
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/terminos-y-condiciones" element={<TermsAndConditions />} />

          <Route element={<ProtectedRoute allowedRoles={['superadmin']} />}>
            <Route path="/superadmin" element={<DashboardLayout><SuperAdminDashboard /></DashboardLayout>} />
            <Route path="/superadmin/talleres" element={<DashboardLayout><SuperAdminTalleres /></DashboardLayout>} />
            <Route path="/superadmin/planes" element={<DashboardLayout><SuperAdminPlanes /></DashboardLayout>} />
            <Route path="/superadmin/facturacion" element={<DashboardLayout><SuperAdminBilling /></DashboardLayout>} />
            <Route path="/superadmin/configuracion" element={<DashboardLayout><SuperAdminPlatformConfig /></DashboardLayout>} />
            <Route
              path="/superadmin/*"
              element={
                <DashboardLayout>
                  <UnderConstructionState
                    title="Este modulo de superadmin aun esta en preparacion"
                    description="Ya dejamos listo el overview, talleres, planes y facturacion SaaS. El siguiente corte entra en automatizaciones, alertas y mas control operativo."
                    backTo="/superadmin"
                    backLabel="Volver al dashboard"
                  />
                </DashboardLayout>
              }
            />
          </Route>

          <Route element={<ProtectedRoute allowedRoles={['admin_taller', 'tecnico']} />}>
            <Route path="/admin" element={<AdminFeatureRoute feature="dashboard"><AdminDashboard /></AdminFeatureRoute>} />
            <Route path="/admin/citas" element={<AdminFeatureRoute feature="citas"><AdminCitas /></AdminFeatureRoute>} />
            <Route path="/admin/ordenes" element={<AdminFeatureRoute feature="ordenes"><AdminOrdenes /></AdminFeatureRoute>} />
            <Route path="/admin/ordenes/:id" element={<AdminFeatureRoute feature="ordenes"><OrdenDetalle /></AdminFeatureRoute>} />
            <Route path="/admin/recordatorios" element={<AdminFeatureRoute feature="recordatorios"><AdminRecordatorios /></AdminFeatureRoute>} />
            <Route path="/admin/inventario" element={<AdminFeatureRoute feature="inventario"><AdminInventario /></AdminFeatureRoute>} />
            <Route path="/admin/clientes" element={<AdminFeatureRoute feature="clientes"><AdminClientes /></AdminFeatureRoute>} />
            <Route path="/admin/vehiculos" element={<AdminFeatureRoute feature="vehiculos"><AdminVehiculos /></AdminFeatureRoute>} />
            <Route path="/admin/cotizaciones" element={<AdminFeatureRoute feature="cotizaciones"><AdminCotizaciones /></AdminFeatureRoute>} />
            <Route
              path="/admin/subscription"
              element={
                <AdminFeatureRoute feature="settings_general" allowedRoles={['admin_taller']}>
                  <AdminSubscription />
                </AdminFeatureRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <AdminFeatureRoute feature="settings_general" allowedRoles={['admin_taller']}>
                  <AdminSettings />
                </AdminFeatureRoute>
              }
            />
            <Route
              path="/admin/upgrade"
              element={
                <AdminFeatureRoute feature="settings_general" allowedRoles={['admin_taller']}>
                  <AdminUpgrade />
                </AdminFeatureRoute>
              }
            />
            <Route
              path="/admin/manual"
              element={
                <DashboardLayout>
                  <AdminManual />
                </DashboardLayout>
              }
            />
            <Route
              path="/admin/*"
              element={
                <DashboardLayout>
                  <UnderConstructionState
                    title="Esta seccion aun no esta lista"
                    description="Todavia estamos afinando este flujo. Te dejamos una salida clara para que no termines en una pantalla vacia."
                    backTo="/admin"
                    backLabel="Volver al dashboard"
                  />
                </DashboardLayout>
              }
            />
          </Route>

          <Route element={<ProtectedRoute allowedRoles={['cliente']} />}>
            <Route path="/client" element={<DashboardLayout><ClientDashboard /></DashboardLayout>} />
            <Route path="/client/vehiculos" element={<DashboardLayout><ClientVehiculos /></DashboardLayout>} />
            <Route path="/client/pagos" element={<DashboardLayout><ClientPagos /></DashboardLayout>} />
            <Route path="/client/citas" element={<DashboardLayout><ClientCitas /></DashboardLayout>} />
            <Route path="/client/cotizaciones" element={<DashboardLayout><ClientCotizaciones /></DashboardLayout>} />
            <Route path="/client/ordenes" element={<DashboardLayout><ClientHistorial /></DashboardLayout>} />
            <Route path="/client/historial" element={<DashboardLayout><ClientHistorial /></DashboardLayout>} />
            <Route path="/client/ordenes/:id" element={<DashboardLayout><OrdenDetalle /></DashboardLayout>} />
            <Route
              path="/client/*"
              element={
                <DashboardLayout>
                  <UnderConstructionState
                    title="Esta vista del portal aun esta en preparacion"
                    description="Estamos trabajando para que el portal del cliente sea mas claro y util. Por ahora puedes regresar a tus modulos principales."
                    backTo="/client"
                    backLabel="Volver a mi portal"
                  />
                </DashboardLayout>
              }
            />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </React.Suspense>
    </BrowserRouter>
  );
}
